<template>
  <b-popover
    custom-class="popover-event-item"
    :triggers="triggers"
    :target="`${type}-event-item-${point.point_id}`"
  >
    <div v-if="nonDefaultPoint" class="___body">
      <div class="__header">
        <div class="__time">{{ formatTimePeriod(point) }}</div>
        <McBadge
          v-if="nonDefaultPoint.badge"
          type="button"
          :text="nonDefaultPoint.badge.text"
          :variant="nonDefaultPoint.badge.variant"
        />
      </div>
      <div v-if="nonDefaultPoint.address" class="__address">
        {{ nonDefaultPoint.address }}
      </div>
    </div>

    <div v-else class="__body">
      <div class="__header">
        <div class="__time">{{ formatTimePeriod(point) }}</div>
        <McBadge
          type="button"
          :text="typeMappings[activityType].text"
          :color="typeMappings[activityType].color"
        />
      </div>
      <div class="__address">{{ point.address.name }}</div>
      <div class="__delivery-orders">
        <div
          v-for="(group, groupIndex) in computedActivities"
          :key="'group-' + groupIndex"
          class="__group"
        >
          <div
            v-for="(activity, activityIndex) in group"
            :key="'activity-' + activity.delivery_order.id || activityIndex"
            :class="{ __error: activity.delivery_order.is_error }"
          >
            <span>
              <i
                class="mc-icon"
                :class="
                  activity.delivery_order.priority === 'high'
                    ? 'icon-fill_star'
                    : 'icon-outline_star'
                "
              />
            </span>
            {{ activity.delivery_order.number }}
          </div>
        </div>
      </div>
      <div v-if="finalActivities.length > 100" class="__other-info">
        +{{ finalActivities.length - 100 }} {{ $t('global.object.other') }}
      </div>
    </div>
  </b-popover>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import McBadge from '@/components/McBadge'

export default {
  components: {
    McBadge,
  },
  props: {
    point: {
      type: Object,
      default: () => ({}),
    },
    triggers: {
      type: String,
      default: 'hover',
    },
    type: {
      type: String,
      default: 'timeline',
    },
  },
  computed: {
    ...mapState('routeOptimization', ['showedRoutes', 'vehiclesRoutes']),

    typeMappings() {
      return {
        PICKUP: {
          text: this.$t('routeOptimization.fields.pick'),
          color: '#1C74DA',
        },
        DROP: {
          text: this.$t('routeOptimization.fields.drop'),
          color: '#137C55',
        },
      }
    },

    nonDefaultPoint() {
      switch (this.point.type) {
        case 'BREAK':
          return {
            badge: { text: this.$t('routeOptimization.fields.breakTime'), variant: 'break' },
          }
        case 'IDLE':
          return { badge: { text: this.$t('routeOptimization.fields.idle'), variant: 'idle' } }
        case 'END':
          return { address: this.$t('routeOptimization.fields.returnToOrigin') }
        default:
          return null
      }
    },

    mergedPickupActivitiesFromRoutes() {
      if (!this.vehiclesRoutes || !this.showedRoutes) return []

      const currentActivityType =
        this.point.activities.length > 0 ? this.point.activities[0].type : 'PICKUP'

      if (currentActivityType !== 'PICKUP') return []

      const allShown = Object.values(this.showedRoutes).every((v) => v === true)
      if (!allShown) return []

      return this.vehiclesRoutes.flatMap((route) => {
        if (!this.showedRoutes[route.id]) return []
        return route.timelines
          .filter((t) => t.type === 'DEFAULT' && t.service_time === 'PT0S')
          .flatMap((t) => t.activities?.filter((a) => a.type === 'PICKUP') || [])
      })
    },

    finalActivities() {
      return this.mergedPickupActivitiesFromRoutes.length > 0
        ? this.mergedPickupActivitiesFromRoutes
        : this.point.activities
    },

    activityType() {
      return this.finalActivities[0]?.type || 'PICKUP'
    },

    computedActivities() {
      const limited = this.finalActivities.slice(0, 100)
      const groups = []
      for (let i = 0; i < limited.length; i += 20) {
        groups.push(limited.slice(i, i + 20))
      }
      return groups
    },
  },
  methods: {
    formatTimePeriod(point) {
      const start = moment(point.estimated_process_on)
      const end = moment(
        point.type === 'END' ? point.estimated_process_on : point.estimated_departure_on
      )

      return point.type === 'END'
        ? end.format('HH:mm')
        : `${start.format('HH:mm')} - ${end.format('HH:mm')}`
    },
  },
}
</script>

<style lang="scss" scoped>
.popover-event-item {
  :deep(.popover-body) {
    background-color: $white;
    border: 1px solid #e6e7ec;
    border-radius: 4px;
    padding: 12px;
    box-shadow: 0px 5px 15px 0px #0d449114;

    .__body {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .__header {
      min-width: 200px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .__time {
      font-size: 12px;
      color: $mep-color-text-400;
    }

    .__address {
      font-weight: 500;
    }

    .__delivery-orders {
      font-size: 12px;
      color: $mep-color-text-400;
      display: flex;
      gap: 20px;
    }

    .__group {
      flex-grow: 1;
    }

    .__other-info {
      font-size: 12px;
      color: $mep-secondary;
    }

    .__error {
      color: $red;
    }
  }

  .mc-icon {
    &::before {
      color: $yellow;
    }
  }
}
</style>
